/**
 * quotePaymentInfo — os três campos de EXIBIÇÃO do orçamento numa fonte única:
 * formas de pagamento aceitas, condições de pagamento e referência externa.
 *
 * Nenhum deles entra no cálculo do total (isso é `src/lib/quoteTotals.ts`, e o
 * grupo 3 não deve ser reaberto). O par builder/leitor daqui é o MESMO padrão
 * de `buildQuoteTotalsColumns`/`quoteHeaderFromRow`: os payloads de `quotes`
 * são montados por LISTA EXPLÍCITA de colunas em ~12 lugares (armadilha
 * #33/#62) e hidratados por lista explícita em 2 (armadilha #70). Um save que
 * não cite `payment_methods` grava com sucesso, sem erro de RLS, com toast de
 * sucesso — e a escolha do usuário some.
 *
 * ─── A REGRA DE NEGÓCIO DO QR PIX (a parte que vai confundir alguém) ─────────
 *
 * O bloco de QR PIX do PDF passou a depender da seleção: se o cliente não pode
 * pagar por PIX, o QR no documento só confunde. Mas "não pode pagar por PIX" e
 * "o prestador nunca abriu esse campo" são coisas DIFERENTES, e é por isso que
 * a coluna distingue `null` de `[]`:
 *
 *   payment_methods === null   → LEGADO / não informado → MOSTRA o QR
 *   payment_methods === []     → escolha explícita       → ESCONDE o QR
 *   payment_methods inclui pix → aceita PIX              → MOSTRA o QR
 *
 * O caso do meio é o único que esconde. A ausência do dado NUNCA pode ser lida
 * como "não aceita PIX": senão todo orçamento anterior a esta feature — e todo
 * PDF que o cliente já recebeu — perderia o QR de um dia para o outro, sem
 * ninguém ter mexido em nada. É o mesmo raciocínio do `products.cost` nulo
 * (armadilha #61): ausência não é zero, e aqui ausência não é recusa.
 *
 * Consequência prática para quem escreve código novo: um caminho que ESQUEÇA de
 * passar `paymentMethods` degrada para o comportamento antigo (QR visível) em
 * vez de apagar o QR. Isso é de propósito — falhar para o lado do histórico —
 * mas significa que o esquecimento é silencioso, e por isso existe o guard de
 * varredura em `src/test/quotePaymentInfo.test.ts`.
 */
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import type { PaymentMethod } from '@/types/database';

/**
 * O vocabulário, em ordem de EXIBIÇÃO (PIX primeiro — é o que o público do app
 * mais usa). É a mesma lista de `finance_entries.payment_method`: os rótulos
 * vêm de `PAYMENT_METHOD_LABELS` e não são redeclarados aqui. Duas listas de
 * códigos para a mesma coisa foi exatamente o desastre das unidades de medida
 * (armadilha #64), onde `hour` do catálogo não existia no orçamento e o Radix
 * mostrava o placeholder num item que TINHA unidade.
 */
export const QUOTE_PAYMENT_METHODS: readonly PaymentMethod[] = [
  'pix',
  'cash',
  'card_credit',
  'card_debit',
  'boleto',
  'transfer',
  'other',
];

const KNOWN = new Set<string>(QUOTE_PAYMENT_METHODS);

/** Os campos de exibição, do formulário. */
export interface QuotePaymentInfo {
  /** `null` = não informado (legado). `[]` = escolha explícita de nenhuma. */
  paymentMethods?: readonly string[] | null;
  paymentTerms?: string | null;
  reference?: string | null;
}

/**
 * Normaliza o que veio do banco, do cache, do `localStorage` do visitante ou da
 * fila offline.
 *
 * PRESERVA a distinção `null` × `[]` — é o ponto inteiro deste módulo. Um
 * `?? []` distraído aqui apagaria o QR PIX de todo o histórico.
 *
 * Códigos desconhecidos são descartados (o banco já os rejeita, mas um cache
 * antigo de outra versão do app pode carregá-los) e a ordem de saída é sempre a
 * canônica, para o PDF não mudar conforme a ordem de clique do usuário.
 */
export function normalizePaymentMethods(raw: unknown): PaymentMethod[] | null {
  if (!Array.isArray(raw)) return null;
  const chosen = new Set(raw.filter((v): v is string => typeof v === 'string' && KNOWN.has(v)));
  return QUOTE_PAYMENT_METHODS.filter((m) => chosen.has(m));
}

/**
 * O documento deve mostrar o bloco de PIX?
 *
 * Ver a tabela no topo do arquivo. `null`/`undefined` → `true` (legado).
 */
export function acceptsPix(methods: unknown): boolean {
  const normalized = normalizePaymentMethods(methods);
  return normalized === null || normalized.includes('pix');
}

/**
 * "PIX, Dinheiro, Boleto" — ou `null` quando não há nada a imprimir.
 *
 * `null` e `[]` colapsam aqui de propósito: nos dois casos o documento
 * simplesmente não ganha a linha "Formas de pagamento aceitas". A diferença
 * entre eles importa só para o QR ({@link acceptsPix}).
 */
export function formatPaymentMethods(methods: unknown): string | null {
  const normalized = normalizePaymentMethods(methods);
  if (!normalized || normalized.length === 0) return null;
  return normalized.map((m) => PAYMENT_METHOD_LABELS[m]).join(', ');
}

const text = (raw: unknown): string => (typeof raw === 'string' ? raw.trim() : '');

/**
 * Os três campos normalizados a partir de uma linha de `quotes` (servidor,
 * cache ou payload pendente na fila offline), prontos para o `formData`.
 *
 * Metade esquecida do problema (armadilha #70): o guard de payloads cobre a
 * ESCRITA. Se a LEITURA perder um campo, a tela reabre sem ele e o próximo save
 * grava `null` — a escolha do usuário desaparece sozinha, sem erro nenhum. Foi
 * exatamente assim que o frete do orçamento de visitante sumiu no grupo 3.
 */
export function quotePaymentInfoFromRow(
  row:
    | {
        payment_methods?: unknown;
        payment_terms?: string | null;
        reference?: string | null;
      }
    | null
    | undefined,
): { paymentMethods: PaymentMethod[] | null; paymentTerms: string; reference: string } {
  return {
    paymentMethods: normalizePaymentMethods(row?.payment_methods),
    paymentTerms: text(row?.payment_terms),
    reference: text(row?.reference),
  };
}

/**
 * As três colunas prontas para um payload de `quotes`.
 *
 * Espalhe (`...buildQuotePaymentColumns(data)`) em vez de repetir as três
 * linhas — é o que impede o próximo campo de nascer esquecido em 4 dos 12
 * caminhos de gravação.
 */
export function buildQuotePaymentColumns(info: QuotePaymentInfo | null | undefined): {
  payment_methods: PaymentMethod[] | null;
  payment_terms: string | null;
  reference: string | null;
} {
  return {
    payment_methods: normalizePaymentMethods(info?.paymentMethods),
    payment_terms: text(info?.paymentTerms) || null,
    reference: text(info?.reference) || null,
  };
}

/**
 * Pré-seleção de um orçamento NOVO.
 *
 * Ordem: o padrão da empresa manda; sem padrão, marca PIX quando a empresa tem
 * chave cadastrada; sem nenhum dos dois, devolve `null` (não informado).
 *
 * No nível da EMPRESA, vazio e ausente são a mesma coisa ("sem padrão") — a
 * distinção `null` × `[]` só faz sentido no documento, onde `[]` é a recusa
 * explícita que esconde o QR.
 *
 * O `null` do último caso é o que torna a transição INVISÍVEL: quem nunca
 * configurar nada continua salvando `payment_methods = null`, e o PDF continua
 * idêntico ao de antes desta feature. E o `['pix']` do caso do meio é o que
 * impede o QR de sumir de quem depende dele mas nunca abriu o bloco novo — sem
 * ele, o primeiro save de um orçamento novo gravaria `[]` e apagaria o QR.
 */
export function defaultPaymentMethodsForNewQuote(
  company: { default_payment_methods?: unknown; pix_key?: string | null } | null | undefined,
): PaymentMethod[] | null {
  const configured = normalizePaymentMethods(company?.default_payment_methods);
  if (configured && configured.length > 0) return configured;
  if (text(company?.pix_key)) return ['pix'];
  return null;
}
